import axios from "axios";
import { Storage } from "@/services/Storage";
import router from "@/router";
import SaleDetails from "@/components/point-of-sale/sales/SaleDetails.vue";
import "core-js/features/array/for-each";
import tableServerClient from "@/services/tableServerClient";
import connectivityMonitor from "@/services/connectivityMonitor";
import OrdersService from "@/services/OrdersService";
import paidTablesOutbox from "@/services/paidTablesOutbox";
// Ticket #26425 — empty-save / merge-name / hydration guards
// docs/design-docs/2026-08-01-ticket-26425-client-merge-investigation.md
// Backend gap-analysis: 2026-08-01-ticket-26425-fix-gap-analysis.md
import {
    applyCommandToSyncPayload,
    applyNameToSyncPayload,
    EMPTY_TABLE_SYNC_BLOCKED_MSG,
    extractSaleNum,
    extractServerTableItems,
    findTableByName,
    isEmptySaveConflictError,
    isTableMissingError,
    mergePendingLocalTablesIntoApiList,
    parseMergeResponse,
    preserveNameOnLocalTableWrite,
    requireMergeTableName,
    resolveConfirmedNameFromResponse,
    shouldBlockEmptySave,
    TABLE_NOT_READY_MSG,
    TABLE_STATE_CONFLICT_MSG,
} from "@/services/tableSyncGuards";
import { responses } from "@/utils/responses";
// Stable cart line id for BE SeperateLine UPSERT-by-id (Path 1)
// docs: FRONTEND.md cart line identity; sales-module addToCurrentSaleLines
import { generateUUID } from "@/utils/uuid";

/** Matches OnlineSalesServer tables list `limit` (max open tables). Ticket 25935. */
const TABLES_LIST_LIMIT_KEY = 'chrilan.tablesListLimit';
const DEFAULT_TABLES_LIST_LIMIT = 10000;
const MIN_TABLES_LIST_LIMIT = 1;
const MAX_TABLES_LIST_LIMIT = 100000;

/**
 * Cloud tables write/read timeout (ms) for bare axios in this module.
 * RESTAURANT-POS-Q hang root cause: axios.post(tables/…) had no timeout, so a
 * stuck CF/backend never settled → isSavingTable stayed true → every later save
 * soft-skipped forever. Finite timeout forces reject → saveNewTable/updateTable
 * finally always clears the lock.
 *
 * Value history: 15s was too aggressive on real sites (large tickets, slow WAN,
 * 409 hydrate+retry). 60s still bounds a true hang without false-failing healthy
 * slow saves. tableServerClient LAN keeps its own REQUEST_TIMEOUT (5s).
 * @see saveNewTable / updateTable finally → setIsSavingTable(false)
 */
const TABLE_CLOUD_TIMEOUT_MS = 60000;

/**
 * True when axios aborted for timeout (default code ECONNABORTED; ETIMEDOUT if
 * transitional.clarifyTimeoutError). Used to map waiter-facing plain language
 * without leaking stack/axios jargon.
 * @param {unknown} err
 * @returns {boolean}
 */
function isAxiosTimeoutError(err) {
    if (!err || typeof err !== 'object') return false;
    const code = err.code;
    if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') return true;
    return /timeout/i.test(String(err.message || ''));
}

function readTablesListLimit() {
    try {
        const n = parseInt(localStorage.getItem(TABLES_LIST_LIMIT_KEY), 10);
        if (Number.isFinite(n) && n >= MIN_TABLES_LIST_LIMIT && n <= MAX_TABLES_LIST_LIMIT) {
            return n;
        }
    } catch (_) { /* ignore */ }
    return DEFAULT_TABLES_LIST_LIMIT;
}

function writeTablesListLimit(n) {
    try {
        localStorage.setItem(TABLES_LIST_LIMIT_KEY, String(n));
    } catch (_) { /* ignore */ }
}

function normalizeTablesListLimit(raw) {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < MIN_TABLES_LIST_LIMIT) return MIN_TABLES_LIST_LIMIT;
    if (n > MAX_TABLES_LIST_LIMIT) return MAX_TABLES_LIST_LIMIT;
    return n;
}

/**
 * B5a defense-in-depth (ATDOPS-23 / SPEC Task B5 + PLAN B5a):
 * Strip count/qty from server-known cooking instructions on the wire payload
 * so backend replace-on-save cannot re-multiply historical FE values.
 *
 * Existing instruction = has `id` OR `cookingNum`/`CookingNum`.
 * New instructions (no server identity) keep full count/qty.
 * Clones items/instructions — does NOT mutate the caller's cart state.
 * Line-level Qty and savedQty are left absolute (print bookkeeping only).
 *
 * @param {Array|null|undefined} items - Sale lines about to POST
 * @returns {Array|null|undefined} Wire-safe items (same ref when not an array)
 * @see Lytning/SPEC.md Step 3 Task B5
 * @see Lytning/PLAN.md Batch 1 B5a
 */
function normalizeInstructionCountsForSave(items) {
    if (!Array.isArray(items)) return items;
    return items.map((item) => {
        if (!item || typeof item !== 'object') return item;
        if (!Array.isArray(item.instructions) || item.instructions.length === 0) {
            return item;
        }
        const instructions = item.instructions.map((instr) => {
            if (!instr || typeof instr !== 'object') return instr;
            const hasServerIdentity =
                (instr.id != null && instr.id !== '') ||
                (instr.cookingNum != null && instr.cookingNum !== '') ||
                (instr.CookingNum != null && instr.CookingNum !== '');
            if (!hasServerIdentity) {
                // New instruction — leave FE count/qty intact
                return instr;
            }
            // Existing instruction — strip historical multipliers for wire only
            const clone = { ...instr };
            delete clone.count;
            delete clone.qty;
            delete clone.Count;
            delete clone.Qty;
            return clone;
        });
        return { ...item, instructions };
    });
}

export default {
    name: 'tables',
    namespaced: false,
    state: {
        allTables: [],
        isTabOpen: false,
        openTableName: null,
        activeSheet: null,
        isSavingTable: false,
        /**
         * Debounce flag for New Sale / New Table entry points.
         * Mirrors processingGRV (Core App GRV debounce) so a rapid double-click
         * cannot fire two empty creates with a half-cleared cart.
         * docs: Online Sales Server TableService.refuseEmptyWipeIfServerHasItems
         */
        isStartingNewSale: false,
        tablesListLimit: readTablesListLimit(),
    },
    getters: {
        getAllTables: (state) => state.allTables,
        getActiveSheet: (state) => state.activeSheet,
        getIsTabOpen: (state) => state.isTabOpen,
        getOpenTableName: (state) => state.openTableName,
        getTablesListLimit: (state) => state.tablesListLimit,
        getIsStartingNewSale: (state) => state.isStartingNewSale,
    },

    mutations: {
        setAllTables: (state, payload) => {
            if (!payload) return Storage.getAllTables().then(res => state.allTables = (res ? res : []))
            state.allTables = payload
        },
        setActiveSheet: (state, data) => state.activeSheet = data,
        setIsTabOpen: (state, data) => state.isTabOpen = data,
        setOpenTableName: (state, data) => state.openTableName = data,
        setIsSavingTable: (state, data) => state.isSavingTable = data,
        setIsStartingNewSale: (state, data) => state.isStartingNewSale = !!data,
        setTablesListLimit: (state, limit) => {
            const n = normalizeTablesListLimit(limit);
            state.tablesListLimit = n;
            writeTablesListLimit(n);
        },
    },
    actions: {
        /**
         * Clear stale saleNumber before navigating to New Sale / New Table.
         * Without this, the BE guard returns 409 EMPTY_SAVE_REJECTED
         * because the previous saleNumber still has server lines.
         * See Online Sales Server TableService.refuseEmptyWipeIfServerHasItems.
         *
         * Resets: currentSale, cart lines, open table name, tip/discount,
         * and localStorage tableReservedSaleNum_* so reserveNextSaleNumber
         * cannot reuse a prior reservation.
         *
         * @param {Object} [payload]
         * @param {boolean} [payload.holdGuard=false] when true, leave
         *   isStartingNewSale=true so the caller (e.g. createNewTable) owns
         *   the double-click lock for the full create duration. Caller MUST
         *   commit setIsStartingNewSale(false) in finally.
         * @returns {Promise<{cleared: boolean, skipped: boolean}>}
         *   skipped=true when a concurrent beginNewSaleSession is already running
         *   (processingNewSale / isStartingNewSale double-click guard).
         */
        beginNewSaleSession: async (context, payload = {}) => {
            if (context.state.isStartingNewSale) {
                console.warn('[TableFlow][beginNewSaleSession] already in progress — ignoring duplicate');
                return { cleared: false, skipped: true };
            }
            const holdGuard = payload && payload.holdGuard === true;
            context.commit('setIsStartingNewSale', true);
            try {
                // Clear stale saleNumber before any New Sale API call.
                // Without this, the BE guard returns 409 EMPTY_SAVE_REJECTED
                // because the previous saleNumber still has server lines.
                // See Online Sales Server TableService.refuseEmptyWipeIfServerHasItems.
                context.commit('clearSale');
                context.commit('setIsTabOpen', false);
                context.commit('setOpenTableName', null);
                context.commit('setOpenTableName', null, { root: true });
                try {
                    context.dispatch('clearLocalStorage');
                } catch (e) {
                    console.warn('[TableFlow][beginNewSaleSession] clearLocalStorage failed:', e?.message || e);
                }
                try {
                    let store = null;
                    try {
                        store = context.getters.getDefaultStore || await Storage.getDefaultStore();
                    } catch (_) {
                        store = null;
                    }
                    const storeDb = store?.StoreDB || 'unknown';
                    const reservedKey = `tableReservedSaleNum_${storeDb}`;
                    localStorage.removeItem(reservedKey);
                    // Belt-and-braces: also drop any other store's reserved key pattern
                    // if StoreDB resolution failed (unknown key only).
                    if (storeDb === 'unknown') {
                        Object.keys(localStorage)
                            .filter((k) => k.startsWith('tableReservedSaleNum_'))
                            .forEach((k) => {
                                try { localStorage.removeItem(k); } catch (_) { /* ignore */ }
                            });
                    }
                } catch (e) {
                    console.warn('[TableFlow][beginNewSaleSession] failed to clear reserved sale number:', e?.message || e);
                }
                console.log('[TableFlow][beginNewSaleSession:cleared]', {
                    currentSaleAfter: context.getters.getCurrentSale,
                    itemCount: Array.isArray(context.getters.getSaleLines) ? context.getters.getSaleLines.length : 0,
                    holdGuard,
                });
                return { cleared: true, skipped: false };
            } finally {
                if (!holdGuard) {
                    context.commit('setIsStartingNewSale', false);
                }
            }
        },

        /**
         * Fetches the current list of open tables and stores it in Vuex state.
         *
         * Behavior depends on the device's role and connectivity:
         *  - Main PC, online: hits the backend REST API directly, then mirrors
         *    the result into the local table-server cache and prunes any local
         *    rows that are no longer present on the server.
         *  - Offline, or non-main PC: reads exclusively from the local
         *    table-server cache (which is the source of truth on non-main
         *    terminals and on the Main PC when offline).
         *
         * Dedupes by sale number, preferring synced/online entries with richer
         * item lists. No-ops with an empty array if no cashier is logged in.
         *
         * Called by `AllTables.vue` on mount, on view activation (e.g. after a
         * payment/void), and on a 30-second polling timer (see
         * `initTablesPolling`) so non-main terminals pick up voids performed on
         * other terminals.
         *
         * @param {Object} context Vuex action context.
         * @param {{ limit?: number }|number|undefined} payload optional max open tables override
         * @returns {Promise<Array>} Resolves with the resolved tables array
         *   (may be empty). The result is also committed to `state.allTables`.
         */
        getAllTables: async (context, payload) => {
            let loggedIn = await auth.isCashierLoggedIn();
            if (!loggedIn) {
                context.commit('setAllTables', []);
                return [];
            }

            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            let settings = await Storage.getDeviceSettings();
            const isOnline = connectivityMonitor.getStatus();

            let limit = context.state.tablesListLimit;
            if (payload != null) {
                if (typeof payload === 'number') {
                    limit = normalizeTablesListLimit(payload);
                    context.commit('setTablesListLimit', limit);
                } else if (typeof payload === 'object' && payload.limit != null) {
                    limit = normalizeTablesListLimit(payload.limit);
                    context.commit('setTablesListLimit', limit);
                }
            }

            // Check if this is the Main PC
            const isMainPC = await tableServerClient.checkIsMainPC();

            // If online AND Main PC, fetch from backend - USE API DATA DIRECTLY
            // This matches Retail POS behavior - no local storage caching for display
            if (isOnline && isMainPC) {
                try {
                    // Finite timeout so a hung list hydrate cannot stall forever
                    // (same RESTAURANT-POS-Q lock-recovery posture as save POST).
                    const { data } = await axios.get(serverApi + `tables/${store.StoreDB}/${user.Username}`, {
                        params: { limit },
                        timeout: TABLE_CLOUD_TIMEOUT_MS,
                    });
                    const tables = data || [];

                    // Build a set of active table saleNumbers from API
                    const activeSaleNums = new Set(
                        tables.map(t => String(t.saleNumber || t.SaleNum)).filter(Boolean)
                    );

                    // Save to local storage (table server) with online flag (for offline fallback)
                    if (tables.length > 0) {
                        try {
                            // Save each table to local storage
                            for (const table of tables) {
                                const tablePayload = {
                                    ...table,
                                    isOffline: false, // Mark as from online
                                    synced: true, // Mark as already synced
                                    SaleNum: table.saleNumber || table.SaleNum,
                                    User: table.user || table.User,
                                    Customer: table.customer || table.Customer,
                                    PC: table.pc || table.PC || settings?.PC || '',
                                    server: table.server || settings?.server || ''
                                };

                                await tableServerClient.addOrUpdateTable(tablePayload, store.StoreDB);
                            }
                        } catch (saveError) {
                            console.error('[Tables] Error saving to local storage:', saveError);
                        }
                    }

                    // Prune local storage: remove synced tables that are no longer on the server
                    // BUG NOTE (investigation 2026-07-11): This loop deletes the local `offline_tables`
                    // SQLite row AND queues a backend DELETE for any table that:
                    //   (a) has `synced === true` (came from the backend on a previous fetch) AND
                    //   (b) is missing from the current `tables/{store}/{user}` response.
                    // It is the INTENDED path for "table closed on backend -> forget it locally".
                    // The OPPOSITE direction (UI has lost the row, backend still has it) is
                    // handled in `AllTables.vue.getOpenTables` line 472-477 by the
                    // `paidTablesOutbox` filter -- which is UI-only and does NOT touch SQLite.
                    // If a table ever ends up "lost from UI/cache but alive in backend SQLite",
                    // this prune cannot recover it on the next sync, and the next
                    // `handleCreateTable` POST will 409 on the LIKE duplicate-name check
                    // (table-server.js handleCreateTable, lines 308-332).
                    let localTablesAfterPrune = [];
                    try {
                        const localTables = await tableServerClient.getAllTables(store.StoreDB, user.Username);
                        for (const localTable of (Array.isArray(localTables) ? localTables : [])) {
                            const localSaleNum = String(localTable?.saleNumber || localTable?.SaleNum || '');
                            if (localSaleNum && localTable.synced && !activeSaleNums.has(localSaleNum)) {
                                console.log('[Tables] Pruning closed table from local storage:', localSaleNum, localTable.Name);
                                await tableServerClient.removeTable(Number(localSaleNum), store.StoreDB, true);
                            }
                        }
                        localTablesAfterPrune = await tableServerClient.getAllTables(store.StoreDB, user.Username);
                    } catch (pruneError) {
                        console.error('[Tables] Error pruning local storage:', pruneError);
                    }

                    // API + pending local (synced !== true) so offline-created tables
                    // stay visible after reconnect until outbox drains to cloud.
                    const displayTables = mergePendingLocalTablesIntoApiList(
                        tables,
                        Array.isArray(localTablesAfterPrune) ? localTablesAfterPrune : []
                    );
                    context.commit('setAllTables', displayTables);
                    return displayTables;
                } catch (error) {
                    console.error('[Tables] Error fetching from backend:', error);
                    // Fall through to local storage on error
                }
            }

            // Offline or not Main PC: use local storage
            try {
                const tables = await tableServerClient.getAllTables(store.StoreDB, user.Username);
                const displayTables = Array.isArray(tables) ? tables : [];

                // Dedupe by SaleNum/saleNumber to avoid duplicate entries after save
                const bySaleNum = new Map();
                for (const table of displayTables) {
                    const saleNum = (table?.saleNumber ?? table?.SaleNum)?.toString();
                    if (!saleNum) continue;

                    const existing = bySaleNum.get(saleNum);
                    if (!existing) {
                        bySaleNum.set(saleNum, table);
                        continue;
                    }

                    // Prefer synced/online entries, then richer item lists
                    const existingScore = (existing?.synced ? 2 : 0) + (existing?.isOffline === false ? 1 : 0);
                    const candidateScore = (table?.synced ? 2 : 0) + (table?.isOffline === false ? 1 : 0);
                    const existingItems = Array.isArray(existing?.items) ? existing.items.length : 0;
                    const candidateItems = Array.isArray(table?.items) ? table.items.length : 0;

                    if (candidateScore > existingScore || (candidateScore === existingScore && candidateItems > existingItems)) {
                        bySaleNum.set(saleNum, table);
                    }
                }

                const deduped = bySaleNum.size > 0 ? Array.from(bySaleNum.values()) : displayTables;
                context.commit('setAllTables', deduped);
                return deduped;
            } catch (error) {
                console.error('[Tables] Error getting tables from local storage:', error);
                context.commit('setAllTables', []);
                return [];
            }
        },
        saveNewTable: async (context, payload) => {
            // Sentry RESTAURANT-POS-Q: concurrent/double-click guard must soft-resolve.
            // Rejecting left unhandled rejections when callers used .then without .catch
            // (or only checked success on resolve). Callers detect result.skipped.
            if (context.state.isSavingTable) {
                console.warn('[saveNewTable] Already saving a table — soft-skip duplicate call');
                return { skipped: true, reason: 'already_saving' };
            }

            try {
                const traceId = `tbl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
                context.commit('setIsSavingTable', true);
                // console.log('[saveNewTable] Starting save with payload:', payload);

                let settings = await Storage.getDeviceSettings();
                let saleLines = context.getters.getSaleLines;
                for (let i = 0; i < saleLines.length; i++) {
                    let line = saleLines[i];
                    saleLines[i].Line = (Number(line.Qty) * Number(line.Unit)) - Number(line.Discount);
                }

                // Prefer an already-reserved cart saleNumber. After a failed invoice,
                // currentSale may already be set by parseInvoice — never mint a second
                // number for that open cart. Route / payload are fallbacks.
                // Name may still be prompted by the UI when missing; name ≠ new sale#.
                // docs: FRONTEND.md — invoice fail → Save-for-later must reuse saleNumber
                const existingSale = Number(context.getters.getCurrentSale || 0);
                const routeSale = Number(router?.currentRoute?.query?.saleNumber || 0);
                const payloadSale = Number(payload?.SaleNum || payload?.saleNumber || 0);
                let saleNumToBind = 0;
                if (Number.isFinite(existingSale) && existingSale > 0) {
                    saleNumToBind = Math.trunc(existingSale);
                } else if (Number.isFinite(payloadSale) && payloadSale > 0) {
                    saleNumToBind = Math.trunc(payloadSale);
                } else if (Number.isFinite(routeSale) && routeSale > 0) {
                    saleNumToBind = Math.trunc(routeSale);
                }

                if (saleNumToBind > 0 && Number(context.getters.getCurrentSale || 0) !== saleNumToBind) {
                    context.commit('setCurrentSale', saleNumToBind);
                }

                // Validate: Check for duplicate table name (only for brand-new tables)
                const tableName = context.getters.getOpenTableName || payload?.name || payload?.Name || null;
                if (tableName && saleNumToBind === 0) {
                    // Filter out paid/voided tables - only check against active tables
                    const paid = await paidTablesOutbox.getAllPaidTables();
                    const paidSet = new Set(
                        (Array.isArray(paid) ? paid : [])
                            .map(p => (p?.saleNumber != null ? String(p.saleNumber).trim() : null))
                            .filter(Boolean)
                    );
                    const allTables = (context.getters.getAllTables || []).filter(t => {
                        const sn = (t?.saleNumber || t?.SaleNum);
                        const snStr = sn != null ? String(sn).trim() : null;
                        return !snStr || !paidSet.has(snStr);  // Exclude paid/voided tables
                    });
                    const existingTable = allTables.find(t =>
                        ((t.Name || t.name) || '').replace(/_offline$/, '').toLowerCase() === tableName.toLowerCase()
                    );
                    if (existingTable) {
                        context.commit('setIsSavingTable', false);
                        throw new Error(`A table with name "${tableName}" already exists. Please use a different name.`);
                    }
                }

                let obj = {
                    // 0 only when truly new — AddOrUpdateTable reserves then;
                    // when >0, binds to existing reserved cart (no second mint)
                    SaleNum: saleNumToBind,
                    Name: tableName,
                    User: context.getters.getCurrentUser.Username,
                    PC: settings.PC,
                    server: settings?.server || '',
                    items: saleLines,
                    Customer: payload.Customer,
                    discount: parseFloat(context.getters.getSaleDiscount.total),
                    orderNumber: payload.orderNumber || null, // Include order number if provided
                    inUse: false, // Mark table as closed after saving
                }

                console.log('[TableFlow][saveNewTable:start]', {
                    traceId,
                    currentSale: context.getters.getCurrentSale,
                    routeSale: router?.currentRoute?.query?.saleNumber,
                    payloadSaleNum: payload?.SaleNum,
                    objSaleNum: obj.SaleNum,
                    itemCount: Array.isArray(obj.items) ? obj.items.length : 0,
                    tableName: obj.Name
                });

                // console.log('[saveNewTable] Calling AddOrUpdateTable with SaleNum:', obj.SaleNum);
                const result = await context.dispatch('AddOrUpdateTable', obj);
                console.log('[TableFlow][saveNewTable:result]', {
                    traceId,
                    returnedSaleNumber: result?.saleNumber,
                    returnedSaleNum: result?.SaleNum,
                    currentSaleAfter: context.getters.getCurrentSale,
                    resultKeys: result ? Object.keys(result) : []
                });
                // console.log('[saveNewTable] Save completed successfully');
                // Clear current sale lines after successful save
                context.commit('clearSale');
                // Close the table - mark as not open
                context.commit('setIsTabOpen', false);
                context.commit('setOpenTableName', null);
                context.commit('setOpenTableName', null, { root: true });

                // CRITICAL: Clear the reserved sale number key so the next table gets a NEW number
                // This prevents duplicate sale numbers when creating multiple offline tables
                try {
                    let store = await Storage.getDefaultStore();
                    const reservedKey = `tableReservedSaleNum_${store?.StoreDB || 'unknown'}`;
                    localStorage.removeItem(reservedKey);
                } catch (e) {
                    console.warn('[saveNewTable] Failed to clear reserved sale number:', e);
                }
                
                return result;
            } catch (err) {
                // Timeout must surface as plain waiter language (not axios/ECONNABORTED).
                // finally below still clears isSavingTable so the lock never sticks.
                if (isAxiosTimeoutError(err)) {
                    throw new Error('Table save timed out. Check connection and try again.');
                }
                // Extract meaningful error message from axios or table server responses
                const msg = err?.response?.data?.error || err?.message || String(err);
                throw new Error(msg);
            } finally {
                // Always clear lock — including after cloud/LAN timeout reject.
                context.commit('setIsSavingTable', false);
            }
        },

        createNewTable: async (context, payload) => {
            const traceId = `tbl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
            // Clear stale saleNumber before New Table create.
            // Without this, the BE guard returns 409 EMPTY_SAVE_REJECTED
            // because the previous saleNumber still has server lines.
            // See Online Sales Server TableService.refuseEmptyWipeIfServerHasItems.
            // WaiterStation / CreateTableView create path must force SaleNum: 0
            // and a NEW reserve — never reuse a leftover currentSale.
            // holdGuard keeps isStartingNewSale locked for the full create.
            const beginResult = await context.dispatch('beginNewSaleSession', { holdGuard: true });
            // Pure concurrency guard (same pattern as beginNewSaleSession / isSavingTable).
            // Soft-resolve so double-clicks do not surface as unhandled rejections in Sentry.
            if (beginResult && beginResult.skipped) {
                console.warn('[createNewTable] New sale already in progress — soft-skip duplicate call');
                return { skipped: true, reason: 'already_starting_new_sale' };
            }

            try {
                let user = await Storage.getCurrentUser();
                let settings = await Storage.getDeviceSettings();
                // Same Main PC probe as Table Server Configuration (not drawer shortcuts).
                await tableServerClient.ensureMainPcReachableForTableWrite();

                // Validate: Check for duplicate table name
                // First refresh the tables list to get the latest data (especially after void)
                await context.dispatch('getAllTables');

                // Filter out paid/voided tables - only check against active tables
                const paid = await paidTablesOutbox.getAllPaidTables();
                const paidSet = new Set(
                    (Array.isArray(paid) ? paid : [])
                        .map(p => (p?.saleNumber != null ? String(p.saleNumber).trim() : null))
                        .filter(Boolean)
                );
                const allTables = (context.getters.getAllTables || []).filter(t => {
                    const sn = (t?.saleNumber || t?.SaleNum);
                    const snStr = sn != null ? String(sn).trim() : null;
                    return !snStr || !paidSet.has(snStr);  // Exclude paid/voided tables
                });

                const tableName = (payload.name && payload.name.length > 0) ? payload.name : "Table";
                const existingTable = allTables.find(t =>
                    ((t.Name || t.name) || '').replace(/_offline$/, '').toLowerCase() === tableName.toLowerCase()
                );
                if (existingTable) {
                    throw new Error(`A table with name "${tableName}" already exists. Please use a different name.`);
                }

                let obj = {
                    // Explicit 0 — create always mints a fresh server reserve via AddOrUpdateTable
                    SaleNum: 0,
                    saleNumber: 0,
                    Name: tableName,
                    User: user.Username,
                    PC: settings.PC,
                    server: settings?.server || '',
                    items: [],
                    Customer: payload.Customer,
                    discount: 0,
                    HeadCount: payload.HeadCount,
                }
                console.log('[TableFlow][createNewTable:start]', {
                    traceId,
                    currentSale: context.getters.getCurrentSale,
                    routeSale: router?.currentRoute?.query?.saleNumber,
                    objSaleNum: obj.SaleNum,
                    itemCount: Array.isArray(obj.items) ? obj.items.length : 0,
                    tableName: obj.Name
                });
                try {
                    const res = await context.dispatch('AddOrUpdateTable', obj);
                    console.log('[TableFlow][createNewTable:result]', {
                        traceId,
                        returnedSaleNumber: res?.saleNumber,
                        returnedSaleNum: res?.SaleNum,
                        // Create path now POSTs empty save so table header exists
                        // (Founder 2026-08-09 — no pendingCreate short-circuit).
                        currentSaleAfter: context.getters.getCurrentSale,
                        resultKeys: res ? Object.keys(res) : []
                    });
                    return res;
                } catch (err) {
                    // EMPTY_SAVE_BLOCKED / silent hydrate — already handled; do not
                    // re-toast "Table Does not Exist" via tomcatError.
                    if (err?.code === 'EMPTY_SAVE_BLOCKED' || err?.silent === true) {
                        throw err;
                    }
                    return errorHandler.tomcatError(err);
                }
            } finally {
                context.commit('setIsStartingNewSale', false);
            }
        },
        printOrders: async (context, payload) => {
            let isAndroid = androidMode;
            if (isAndroid) return;
            let settings = await Storage.getDeviceSettings();
            let store = await Storage.getDefaultStore();
            let user = null;
            try {
                user = await Storage.getCurrentUser();
            } catch (e) { user = null; }

            let obj = {
                set: settings,
                log: payload,
                options: {
                    cashier: user?.Username || user?.username || '',

                    separators: true
                }
            }
            try {
                const response = await axios.post(printVoid + `printing/${store.StoreDB}/printOrders`, obj);


                if (androidMode) {
                    return window.location.href = `rawbt:base64,${response.data}`
                }

                if (response.data) {
                    try {
                        response.data.forEach(data => {
                            let payload = {
                                printer: data.printer,
                                base64: data.base64
                            };
                            return axios.post(hardwareApi + 'printing/base64', payload);
                        });
                    }
                    catch {
                        return responses.showWarning('Printin order error')
                    }

                } else {
                    return responses.showWarning('No printer selected')
                }

            } catch (err) {
                errorHandler.tomcatError(err, false, `Orders Print Error`)
            }

        },
        AddOrUpdateTable: async (context, payload) => {
            const traceId = `tbl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
            const suppressPrint = payload?.suppressPrint === true;
            console.log('[TableFlow][AddOrUpdateTable:start]', {
                traceId,
                SaleNum: payload.SaleNum,
                Name: payload.Name,
                isOffline: payload.isOffline,
                synced: payload.synced,
                itemCount: Array.isArray(payload?.items) ? payload.items.length : 0,
                currentSale: context.getters.getCurrentSale,
                routeSale: router?.currentRoute?.query?.saleNumber,
                stackTrace: new Error().stack
            });
            console.log('[TableFlow][AddOrUpdateTable] suppressPrint ===', suppressPrint, '| payload.suppressPrint ===', payload.suppressPrint);

            let isAllowed = await context.dispatch(`validateSecurityPermission`, `SaveTable`);
            if (!isAllowed) throw new Error(`User not allowed to Save Tables`);

            let store = await Storage.getDefaultStore();
            let settings = await Storage.getDeviceSettings();
            // Same Main PC probe as Table Server Configuration (LAN/host only; Direct Backend skips).
            const isMainPC = await tableServerClient.checkIsMainPC();
            let isOnline = connectivityMonitor.getStatus();

            if (isMainPC) {
                try {
                    isOnline = await connectivityMonitor.forceCheck();
                } catch (e) {
                    isOnline = connectivityMonitor.getStatus();
                }
            }

            await tableServerClient.ensureMainPcReachableForTableWrite();
            // Non-main LAN clients follow backend reachability published via Main PC health.
            if (!isMainPC) {
                isOnline = connectivityMonitor.getStatus();
            }

            // Fetch the authenticated cashier once. The backend requires the
            // `user` field on every payload sent to
            // POST /OnlineSalesServer/tables/{storeDb} — without it the server
            // responds with 400 "user account not detected". Same pattern is
            // used by parseInvoice (sales-module.js L1574, L1630). The caller
            // (e.g. SearchedProducts.updateTable) may also attach `user`; we
            // re-attach here as a belt-and-braces guard so the contract holds
            // regardless of which component invoked us.
            let currentUser = null;
            try {
                currentUser = await Storage.getCurrentUser();
            } catch (e) {
                currentUser = null;
            }
            const resolvedUser =
                (currentUser && (currentUser.Username || currentUser.username)) ||
                (payload && (payload.user || payload.User)) ||
                '';

            let workingPayload = {
                ...payload,
                user: resolvedUser,
            };
            const requestedSaleNum = Number(workingPayload.SaleNum || workingPayload.saleNumber || 0);
            if (!Number.isFinite(requestedSaleNum) || requestedSaleNum <= 0) {
                try {
                    const reserved = await context.dispatch('reserveNextSaleNumber', { strictOnline: isOnline });
                    const saleNum = Math.trunc(Number(reserved));
                    if (!Number.isFinite(saleNum) || saleNum <= 0) {
                        throw new Error('Invalid sale number response');
                    }
                    workingPayload.SaleNum = saleNum;
                    workingPayload.saleNumber = saleNum;
                    if (!(Number(workingPayload.orderNumber || 0) > 0)) {
                        workingPayload.orderNumber = saleNum;
                    }
                    console.log('[TableFlow][SaleNumAssigned][tables/write]', {
                        traceId,
                        storeDB: store.StoreDB,
                        saleNum
                    });
                } catch (seedErr) {
                    throw new Error(`Failed to allocate sale number from Main device: ${seedErr?.message || seedErr}`);
                }
            }

            /**
             * Ticket #26425 Fix A — command-aware empty-save guard.
             * Ordinary save of an EXISTING SaleNum with items=[] wiped merged lines
             * (Zest Bar). Re-fetch server lines when local cart is empty; never POST
             * empty replace on a pre-existing sale.
             *
             * Founder 2026-08-09 (Joseph WaiterStation 2.1.12 regression):
             * When saleNum was JUST allocated in this call (requestedSaleNum was 0),
             * BE only reserved the number — it does NOT create the table row until
             * an actual save (even empty) is POSTed. Blocking that empty create
             * left later code paths calling savetable/{saleNum} → "Table Does not Exist".
             * saleNumJustAllocated=true skips the wipe guard so create can POST.
             * Split/void/clear still bypass via command. See shouldBlockEmptySave.
             */
            /**
             * Custom table-name create fix (ticket table-name bug):
             * Do NOT fall back to getOpenTableName here. On saveNewTable the user
             * already committed setOpenTableName(typedName) before dispatch, so
             * previous === proposed and applyNameToSyncPayload strips Name —
             * backend then defaults to "Table #X".
             * For new tables (no prior server name), forceRename so Name is kept.
             * docs: tableSyncGuards.resolveNameForSyncPayload
             */
            const previousNameForSync =
                workingPayload.previousName ??
                workingPayload._previousName ??
                null;
            // New table = no prior server name on the payload (SaleNum may still
            // be pre-allocated). forceRename ensures typed Name is not stripped.
            const isNewTable = !workingPayload.previousName && !workingPayload._previousName;
            applyCommandToSyncPayload(workingPayload, {
                command: workingPayload.command,
                expectedRevision: workingPayload.expectedRevision ?? workingPayload.revision,
            });
            applyNameToSyncPayload(workingPayload, previousNameForSync, {
                forceRename:
                    isNewTable ||
                    workingPayload.rename === true ||
                    workingPayload.forceRename === true,
            });

            const guardSaleNum = Number(workingPayload.SaleNum || workingPayload.saleNumber || 0);
            // True when this call minted SaleNum from 0 — CREATE path, allow empty POST.
            const saleNumJustAllocated =
                !Number.isFinite(requestedSaleNum) || requestedSaleNum <= 0;
            if (
                shouldBlockEmptySave(
                    workingPayload.command,
                    guardSaleNum,
                    workingPayload.items,
                    { saleNumJustAllocated }
                )
            ) {
                console.warn('[TableFlow][AddOrUpdateTable:emptySaveBlocked]', {
                    traceId,
                    saleNum: guardSaleNum,
                    command: workingPayload.command,
                    saleNumJustAllocated,
                });

                // Existing SaleNum with empty local cart: re-fetch server lines (silent —
                // never toast "Table Does not Exist" from hydrate).
                try {
                    const serverTable = await context.dispatch('getTableData', {
                        saleNumber: guardSaleNum,
                        silent: true,
                    });
                    const serverItems = extractServerTableItems(serverTable);
                    if (serverItems.length > 0) {
                        workingPayload.items = serverItems;
                        // Prefer server name when we hydrated from authoritative state
                        const serverName = resolveConfirmedNameFromResponse(serverTable, previousNameForSync);
                        if (serverName) {
                            workingPayload.Name = serverName;
                            applyNameToSyncPayload(workingPayload, serverName, { forceRename: false });
                        }
                        if (serverTable?.revision != null) {
                            workingPayload.expectedRevision = Number(serverTable.revision);
                        }
                        console.log('[TableFlow][AddOrUpdateTable:emptySaveHydrated]', {
                            traceId,
                            saleNum: guardSaleNum,
                            itemCount: serverItems.length,
                        });
                    } else {
                        // Server also empty — block wipe, no scary error toast
                        console.warn('[TableFlow][AddOrUpdateTable:emptySaveServerEmpty]', {
                            traceId,
                            saleNum: guardSaleNum,
                        });
                        const err = new Error(EMPTY_TABLE_SYNC_BLOCKED_MSG);
                        err.code = 'EMPTY_SAVE_BLOCKED';
                        err.suppressToast = true;
                        err.silent = true;
                        throw err;
                    }
                } catch (hydrateErr) {
                    if (hydrateErr?.code === 'EMPTY_SAVE_BLOCKED') throw hydrateErr;
                    console.error('[TableFlow][AddOrUpdateTable:emptySaveRefetchFailed]', hydrateErr);
                    const missing = isTableMissingError(hydrateErr);
                    const err = new Error(missing ? TABLE_NOT_READY_MSG : EMPTY_TABLE_SYNC_BLOCKED_MSG);
                    err.code = 'EMPTY_SAVE_BLOCKED';
                    // Missing table after empty save is expected race — silent for operators
                    err.silent = true;
                    err.suppressToast = true;
                    throw err;
                }
            } else if (saleNumJustAllocated) {
                // Create path with empty items — empty POST will create the table row.
                console.log('[TableFlow][AddOrUpdateTable:emptyCreateAllowed]', {
                    traceId,
                    saleNum: guardSaleNum,
                    name: workingPayload.Name || workingPayload.name || null,
                });
            }

            // B5a — strip stale instruction count/qty before any online/offline POST.
            // Applied after empty-save hydration so server-hydrated lines are covered too.
            // Online + offline paths both read workingPayload.items.
            if (Array.isArray(workingPayload.items)) {
                workingPayload.items = normalizeInstructionCountsForSave(workingPayload.items);

                /**
                 * Ticket 22844 — await line-id hydrate BEFORE minting UUIDs on existing
                 * online sales. All Tables list returns display-only lines (no currentsale
                 * PK). TableDetails.hydrateLineIdentities is fire-and-forget on mount, so
                 * Save can race: missing id → generateUUID() → BE INSERT new row while the
                 * old active row remains → qty doubling on every save.
                 * True new cart lines already receive UUID in addToCurrentSaleLines; only
                 * mint here for brand-new tables or offline paths that never had server ids.
                 * docs: FRONTEND.md cart line identity; BE SeperateLine Path 1 UPSERT-by-id
                 */
                const identitySaleNum = Number(
                    workingPayload.SaleNum || workingPayload.saleNumber || 0
                );
                const isExistingSaleIdentity =
                    Number.isFinite(identitySaleNum) && identitySaleNum > 0;
                const lineHasIdentity = (it) =>
                    !!(it && (it.id || it.Id || it.lineId || it.currentSaleLineId));

                if (isOnline && isExistingSaleIdentity) {
                    const needsLineIdentityHydrate = workingPayload.items.some(
                        (it) => it && !lineHasIdentity(it)
                    );
                    if (needsLineIdentityHydrate) {
                        try {
                            const serverTable = await context.dispatch(
                                'getTableData',
                                identitySaleNum
                            );
                            const detailItems = extractServerTableItems(serverTable);
                            if (Array.isArray(detailItems) && detailItems.length > 0) {
                                // Same PLU+desc then PLU then index match as TableDetails.hydrateLineIdentities
                                const byKey = new Map();
                                detailItems.forEach((line, idx) => {
                                    if (!line) return;
                                    const plu = String(line.Plu || line.plu || '');
                                    const desc = String(
                                        line.Description || line.description || ''
                                    );
                                    const keyExact = `${plu}::${desc}`;
                                    if (!byKey.has(keyExact)) byKey.set(keyExact, []);
                                    byKey.get(keyExact).push(line);
                                    const keyPlu = `${plu}::`;
                                    if (!byKey.has(keyPlu)) byKey.set(keyPlu, []);
                                    byKey.get(keyPlu).push(line);
                                    byKey.set(`idx::${idx}`, line);
                                });
                                workingPayload.items.forEach((item, idx) => {
                                    if (!item || lineHasIdentity(item)) return;
                                    const plu = String(item.Plu || item.plu || '');
                                    const desc = String(
                                        item.Description || item.description || ''
                                    );
                                    const exactBucket = byKey.get(`${plu}::${desc}`) || [];
                                    const pluBucket = byKey.get(`${plu}::`) || [];
                                    const match =
                                        exactBucket.shift() ||
                                        pluBucket.shift() ||
                                        byKey.get(`idx::${idx}`);
                                    if (!match) return;
                                    const id =
                                        match.id ||
                                        match.Id ||
                                        match.lineId ||
                                        match.currentSaleLineId ||
                                        null;
                                    const saleline =
                                        match.saleline != null
                                            ? match.saleline
                                            : match.SaleLine != null
                                              ? match.SaleLine
                                              : match.saleLine != null
                                                ? match.saleLine
                                                : null;
                                    if (id) {
                                        item.id = id;
                                    }
                                    if (saleline != null && item.saleline == null) {
                                        item.saleline = saleline;
                                    }
                                });
                            }
                            console.log(
                                '[TableFlow][AddOrUpdateTable:lineIdentityHydrated]',
                                {
                                    traceId,
                                    saleNum: identitySaleNum,
                                    stillMissing: workingPayload.items.filter(
                                        (it) => it && !lineHasIdentity(it)
                                    ).length,
                                }
                            );
                        } catch (hydrateIdentityErr) {
                            console.error(
                                '[TableFlow][AddOrUpdateTable:lineIdentityHydrateFailed]',
                                hydrateIdentityErr
                            );
                            // Online existing sale: refuse blind mint that would double rows
                            const err = new Error(
                                'Could not identify all lines. Please try again or reopen the table.'
                            );
                            err.code = 'LINE_IDENTITY_HYDRATE_FAILED';
                            try {
                                responses.showError(
                                    'Could not identify all lines. Please try again or reopen the table.'
                                );
                            } catch (_) {
                                /* toast optional */
                            }
                            throw err;
                        }

                        // After hydrate, any remaining missing-id lines on an online
                        // existing sale must not be minted — that is the doubling vector.
                        // Cart-added new lines already carry UUID from addToCurrentSaleLines.
                        const stillMissingIdentity = workingPayload.items.filter(
                            (it) => it && !lineHasIdentity(it)
                        );
                        if (stillMissingIdentity.length > 0) {
                            console.error(
                                '[TableFlow][AddOrUpdateTable:lineIdentityStillMissing]',
                                {
                                    traceId,
                                    saleNum: identitySaleNum,
                                    missingCount: stillMissingIdentity.length,
                                }
                            );
                            const err = new Error(
                                'Some lines could not be identified. Please reopen the table and try again.'
                            );
                            err.code = 'LINE_IDENTITY_MISSING';
                            try {
                                responses.showError(
                                    'Some lines could not be identified. Please reopen the table and try again.'
                                );
                            } catch (_) {
                                /* toast optional */
                            }
                            throw err;
                        }
                    }
                }

                // Stamp stable client id + 1-indexed saleline on every line before
                // POST /tables/{DB}. Backend SeperateLine Path 1 (UPSERT by id)
                // requires both; without them Path 3 PLU-only match collapses
                // same-PLU multi-price lines into one row.
                // docs: FRONTEND.md cart line identity; BE CurrentSale.SeperateLine
                // Ticket 22844: only mint UUID for true new tables / offline — never
                // for online existing-sale lines (guarded above).
                workingPayload.items.forEach((item, i) => {
                    if (!item) return;
                    if (!item.id && !item.Id && !item.lineId && !item.currentSaleLineId) {
                        // Safety net: online existing sale must never reach here with
                        // missing ids (aborted above). Mint only new-table / offline.
                        if (isOnline && isExistingSaleIdentity) {
                            const err = new Error(
                                'Cannot save — lines missing identity. Reopen the table and try again.'
                            );
                            err.code = 'LINE_IDENTITY_MISSING';
                            try {
                                responses.showError(
                                    'Cannot save — lines missing identity. Reopen the table and try again.'
                                );
                            } catch (_) {
                                /* toast optional */
                            }
                            throw err;
                        }
                        item.id = generateUUID();
                    } else if (!item.id) {
                        item.id = item.Id || item.lineId || item.currentSaleLineId;
                    }
                    if (item.saleline == null && item.SaleLine == null && item.saleLine == null) {
                        item.saleline = i + 1;
                    } else if (item.saleline == null) {
                        item.saleline = item.SaleLine != null ? item.SaleLine : item.saleLine;
                    }
                });
            }

            /**
             * POST tables save with one automatic 409 recovery attempt.
             * On EMPTY_SAVE_REJECTED / TABLE_STATE_CONFLICT: re-fetch once, retry with
             * hydrated items (never retry the empty body). Victoria brief Fix A.
             *
             * @param {object} body - online payload
             * @param {boolean} allowRetry - whether a 409 re-fetch retry is still allowed
             * @returns {Promise<object>} axios response data
             */
            const postTableSaveWithConflictRetry = async (body, allowRetry = true) => {
                try {
                    // MUST have timeout: hang here left isSavingTable true forever (RESTAURANT-POS-Q).
                    // Reject → AddOrUpdateTable catch / offline fallback or outer finally clears lock.
                    const { data } = await axios.post(
                        serverApi + `tables/${store.StoreDB}`,
                        body,
                        { timeout: TABLE_CLOUD_TIMEOUT_MS }
                    );
                    return data;
                } catch (postErr) {
                    if (!allowRetry || !isEmptySaveConflictError(postErr)) {
                        throw postErr;
                    }
                    console.warn('[TableFlow][AddOrUpdateTable:409Conflict]', {
                        traceId,
                        saleNum: body.SaleNum || body.saleNumber,
                        message: TABLE_STATE_CONFLICT_MSG,
                    });
                    try {
                        responses.showWarning(TABLE_STATE_CONFLICT_MSG, 3000);
                    } catch (_) { /* toast optional */ }
                    const sn = Number(body.SaleNum || body.saleNumber || 0);
                    const serverTable = await context.dispatch('getTableData', sn);
                    const serverItems = extractServerTableItems(serverTable);
                    if (!serverItems.length) {
                        // Do not retry empty body — Marcus §6.2 / Victoria Fix A
                        const err = new Error(EMPTY_TABLE_SYNC_BLOCKED_MSG);
                        err.code = 'EMPTY_SAVE_BLOCKED';
                        throw err;
                    }
                    const retryBody = {
                        ...body,
                        // B5a — normalize 409-retry items the same as the primary path
                        items: normalizeInstructionCountsForSave(serverItems),
                    };
                    applyCommandToSyncPayload(retryBody, { command: body.command || 'save' });
                    if (serverTable?.revision != null) {
                        retryBody.expectedRevision = Number(serverTable.revision);
                    }
                    const confirmed = resolveConfirmedNameFromResponse(serverTable, body.Name || body.name);
                    if (confirmed) {
                        retryBody.Name = confirmed;
                        applyNameToSyncPayload(retryBody, confirmed, { forceRename: false });
                    }
                    // Single retry only — never loop
                    return postTableSaveWithConflictRetry(retryBody, false);
                }
            };

            // Only Main PC should sync directly to backend
            if (isOnline && isMainPC) {
                try {
                    const onlinePayload = { ...workingPayload };
                    const currentSaleNum = Number(onlinePayload.SaleNum || onlinePayload.saleNumber || 0);
                    if (!Number.isFinite(currentSaleNum) || currentSaleNum <= 0) {
                        try {
                            const saleSeedData = await tableServerClient.proxyGetSaleNum(store.StoreDB);
                            const rawSeed = saleSeedData?.response ?? saleSeedData?.saleNum ?? saleSeedData?.saleNumber ?? saleSeedData;
                            const assignedSaleNum = Number(rawSeed);
                            if (Number.isFinite(assignedSaleNum) && assignedSaleNum > 0) {
                                const saleNum = Math.trunc(assignedSaleNum);
                                onlinePayload.SaleNum = saleNum;
                                onlinePayload.saleNumber = saleNum;
                                if (!(Number(onlinePayload.orderNumber || 0) > 0)) {
                                    onlinePayload.orderNumber = saleNum;
                                }
                                console.log('[TableFlow][SaleNumAssigned][tables/save]', {
                                    traceId,
                                    storeDB: store.StoreDB,
                                    saleNum
                                });
                            } else {
                                throw new Error('Invalid sale number response from backend');
                            }
                        } catch (seedErr) {
                            throw new Error(`Failed to allocate sale number from Main device: ${seedErr?.message || seedErr}`);
                        }
                    }
                    const finalSaleNum = Number(onlinePayload.SaleNum || onlinePayload.saleNumber || 0);
                    if (!Number.isFinite(finalSaleNum) || finalSaleNum <= 0) {
                        throw new Error('Main device did not allocate a valid sale number for table save.');
                    }

                    console.log('[TableFlow][AddOrUpdateTable:onlineAttempt]', {
                        traceId,
                        payloadSaleNum: finalSaleNum,
                        command: onlinePayload.command,
                        itemCount: Array.isArray(onlinePayload.items) ? onlinePayload.items.length : 0,
                        hasName: onlinePayload.Name != null || onlinePayload.name != null,
                    });
                    const data = await postTableSaveWithConflictRetry(onlinePayload, true);
                    // Ticket #26425 Fix C — server is authoritative for persisted name
                    const confirmedName = resolveConfirmedNameFromResponse(
                        data,
                        onlinePayload.Name || onlinePayload.name || previousNameForSync
                    );
                    if (confirmedName) {
                        onlinePayload.Name = confirmedName;
                        try {
                            context.commit('setOpenTableName', confirmedName);
                        } catch (_) { /* optional */ }
                    }

                    // Success! Now update local cache (table server)
                    // Mark as synced so it doesn't get queued again
                    // IMPORTANT: Use data from backend response to get the correct SaleNum
                    const localPayload = {
                        ...onlinePayload,
                        ...(data && typeof data === 'object' ? data : {}), // Merge backend response
                        SaleNum: (data && (data.SaleNum || data.saleNumber || data.saleNum)) || onlinePayload.SaleNum,
                        isOffline: false,
                        synced: true
                    };
                    if (confirmedName) {
                        localPayload.Name = confirmedName;
                        localPayload.name = confirmedName;
                    }

                    // Update currentSale with the SaleNum from backend response
                    if (data && (data.SaleNum || data.saleNumber || data.saleNum)) {
                        const newSaleNum = data.SaleNum || data.saleNumber || data.saleNum;
                        // console.log(`[Tables] Updating currentSale from ${context.getters.getCurrentSale} to ${newSaleNum}`);
                        context.commit('setCurrentSale', newSaleNum);
                        console.log('[TableFlow][AddOrUpdateTable:onlineSetCurrentSale]', {
                            traceId,
                            previous: payload?.SaleNum,
                            updatedTo: newSaleNum
                        });
                    }

                    // AD-4: suppressPrint absolute — skip all kitchen print when true.
                    // AD-3: Main keeps Vuex cloud printOrders + local ESC/POS; non-Main kitchen paper = LAN only.
                    if (data && typeof data === 'object' && data.log && !suppressPrint && isMainPC) {
                        // Only print on update if there are adjusted items in the log
                        let logItems = Array.isArray(data.log) ? data.log : [];
                        const hasAdjustment = (line) => {
                            try {
                                const notes = line?.Notes || line?.note || line?.Comment;
                                const instructions = Array.isArray(line?.instructions) ? line.instructions : [];
                                const flag = line?.adjusted || line?.hasAdjustment || line?.modified;
                                return (notes && notes.toString().trim().length > 0) || instructions.length > 0 || flag === true;
                            } catch (_) { return false; }
                        };
                        const adjustedOnly = logItems.filter(hasAdjustment);
                        const isUpdate = payload.isUpdate || (payload.SaleNum && payload.SaleNum > 0);
                        const shouldPrint = !isUpdate || adjustedOnly.length > 0;
                        if (shouldPrint) {
                            context.dispatch(`printOrders`, isUpdate ? adjustedOnly : data.log);
                        }
                    }

                    // Kitchen paper after save (online Main path; defensive isMainPC branch for Hybrid A).
                    // AD-2 LAN payload: { items, tableName, saleNumber, cashier, orderNumber, separators }
                    // AD-5: new table = full lines; update = qty-delta only (filter before LAN forward).
                    try {
                        const tableName = payload?.Name || context.getters.getOpenTableName;
                        const lines = Array.isArray(payload?.items) ? payload.items : [];
                        console.log("lines", lines);
                        // Enrich items with StockSetting if missing
                        for (const line of lines) {
                            if (!line.StockSetting && !line.stockSetting) {
                                try {
                                    const setting = await Storage.getStockSetting(line.Plu || line.plu);
                                    // console.log("setting", setting);
                                    if (setting) line.StockSetting = setting;
                                } catch (e) { console.warn('Failed to fetch stock setting for print', e); }
                            }
                        }
                        const isUpdate = payload.isUpdate || (payload.SaleNum && payload.SaleNum > 0);
                        if (lines.length > 0) {
                            console.log(`[Tables] First Item Debug: Qty=${lines[0].Qty}, savedQty=${lines[0].savedQty}, Type of savedQty=${typeof lines[0].savedQty}`);
                        }
                        const toPrint = isUpdate ? lines.filter(l => (Number(l.Qty) - Number(l.savedQty || 0)) > 0).map(l => ({ ...l, Qty: Number(l.Qty) - Number(l.savedQty || 0) })) : lines;
                        console.log(`[Tables] Print Logic: isUpdate=${isUpdate}, Total Lines=${lines.length}, To Print=${toPrint.length}`);
                        if (toPrint.length > 0 && !suppressPrint) {
                            let userForPrint = null;
                            try { userForPrint = await Storage.getCurrentUser(); } catch (e) { userForPrint = null; }
                            const cashier = userForPrint?.Username || userForPrint?.username || '';
                            const saleNumber = data.SaleNum || data.saleNumber || payload.SaleNum;
                            if (isMainPC) {
                                // Main: existing local ESC/POS (Printer1–N on this device)
                                await OrdersService.printOrder(toPrint, {
                                    tableName,
                                    cashier,
                                    saleNumber,
                                    separators: true
                                });
                            } else {
                                // Non-Main (tablet): LAN → Main table-server POST /tables/printOrders (Hybrid A).
                                // Do NOT call local OrdersService.printOrder (no hardwareIPC on tablet).
                                try {
                                    await tableServerClient.printOrders({
                                        items: toPrint,
                                        tableName,
                                        saleNumber,
                                        cashier,
                                        orderNumber: saleNumber,
                                        separators: true
                                    }, store.StoreDB);
                                } catch (lanErr) {
                                    // AD-8: print failure non-fatal after save success
                                    console.warn('[Tables] LAN printOrders failed (online path, non-Main):', lanErr);
                                }
                            }
                        }
                    } catch (e) {
                        console.warn('[Tables] Local order print failed (online path):', e);
                    }
                    context.commit('updateSavedQty', null, { root: true });
                    console.log('[TableFlow][AddOrUpdateTable:onlineDone]', {
                        traceId,
                        returnedSaleNumber: data?.saleNumber,
                        returnedSaleNum: data?.SaleNum,
                        currentSaleAfter: context.getters.getCurrentSale
                    });

                    return data;
                } catch (error) {
                    console.error('[Tables] Backend sync failed, falling back to offline mode:', error);
                    // Continue to offline fallback...
                }
            }


            try {
                console.log('[TableFlow][AddOrUpdateTable:offlineRoute]', {
                    traceId,
                    isMainPC,
                    isOnline,
                    payloadSaleNum: payload?.SaleNum
                });

                // Mark as offline and NOT synced
                const offlinePayload = {
                    ...workingPayload,
                    // Mark offline only when the device is actually offline
                    isOffline: !isOnline,
                    synced: false
                };
                // Fix C may have deleted Name (omit when unchanged for cloud). Local
                // SQLite full-replaces table_data — restore name so second save keeps title.
                preserveNameOnLocalTableWrite(
                    offlinePayload,
                    previousNameForSync
                        || context.getters.getOpenTableName
                        || payload?.Name
                        || payload?.name
                        || null
                );

                // Route through table server client
                const result = await tableServerClient.addOrUpdateTable(offlinePayload, store.StoreDB);
                console.log('[TableFlow][AddOrUpdateTable:offlineResult]', {
                    traceId,
                    resultSaleNumber: result?.saleNumber,
                    resultSaleNum: result?.SaleNum,
                    currentSaleBeforeSet: context.getters.getCurrentSale,
                    resultKeys: result ? Object.keys(result) : []
                });

                // CRITICAL: Update currentSale with the SaleNum from the result
                // This prevents creating duplicate tables on subsequent saves
                if (result && result.saleNumber) {
                    console.log(`[Tables] Updating currentSale from ${context.getters.getCurrentSale} to ${result.saleNumber}`);
                    context.commit('setCurrentSale', result.saleNumber);
                } else if (result && result.SaleNum) {
                    console.log(`[Tables] Updating currentSale from ${context.getters.getCurrentSale} to ${result.SaleNum}`);
                    context.commit('setCurrentSale', result.SaleNum);
                }

                // AD-4: suppressPrint absolute — skip all kitchen print when true.
                // AD-3: Main keeps Vuex cloud printOrders; non-Main skips cloud (kitchen paper = LAN only).
                if (result.log && !suppressPrint && isMainPC) {
                    // Only print on update if there are adjusted items in the log
                    let logItems = Array.isArray(result.log) ? result.log : [];
                    const hasAdjustment = (line) => {
                        try {
                            const notes = line?.Notes || line?.note || line?.Comment;
                            const instructions = Array.isArray(line?.instructions) ? line.instructions : [];
                            const flag = line?.adjusted || line?.hasAdjustment || line?.modified;
                            return (notes && notes.toString().trim().length > 0) || instructions.length > 0 || flag === true;
                        } catch (_) { return false; }
                    };
                    const adjustedOnly = logItems.filter(hasAdjustment);
                    const isUpdate = payload.isUpdate || (payload.SaleNum && payload.SaleNum > 0);
                    const shouldPrint = !isUpdate || adjustedOnly.length > 0;
                    if (shouldPrint) {
                        context.dispatch(`printOrders`, isUpdate ? adjustedOnly : result.log);
                    }
                }

                // Kitchen paper after save (Branch B: offline Main + all non-Main tablet saves).
                // AD-2 LAN payload: { items, tableName, saleNumber, cashier, orderNumber, separators }
                // AD-5: new table = full lines; update = qty-delta only (filter before LAN forward).
                try {
                    const tableName = payload?.Name || context.getters.getOpenTableName;
                    const lines = Array.isArray(payload?.items) ? payload.items : [];
                    // Enrich items with StockSetting if missing
                    for (const line of lines) {
                        if (!line.StockSetting && !line.stockSetting) {
                            try {
                                const setting = await Storage.getStockSetting(line.Plu || line.plu);
                                if (setting) line.StockSetting = setting;
                            } catch (e) { console.warn('Failed to fetch stock setting for print', e); }
                        }
                    }
                    const isUpdate = payload.isUpdate || (payload.SaleNum && payload.SaleNum > 0);
                    const toPrint = isUpdate ? lines.filter(l => (Number(l.Qty) - Number(l.savedQty || 0)) > 0).map(l => ({ ...l, Qty: Number(l.Qty) - Number(l.savedQty || 0) })) : lines;
                    console.log(`[Tables] Offline Print Logic: isUpdate=${isUpdate}, Total Lines=${lines.length}, To Print=${toPrint.length}`);
                    if (!suppressPrint && toPrint.length > 0) {
                        let userForPrint = null;
                        try { userForPrint = await Storage.getCurrentUser(); } catch (e) { userForPrint = null; }
                        const cashier = userForPrint?.Username || userForPrint?.username || '';
                        const saleNumForPrint = result.saleNumber || result.SaleNum || payload.SaleNum;
                        if (isMainPC) {
                            // Main: existing local ESC/POS (unchanged options for offline path)
                            await OrdersService.printOrder(toPrint, {
                                tableName,
                                saleNumber: saleNumForPrint,
                                // Offline: use the same sale number as the order number
                                orderNumber: saleNumForPrint
                            });
                        } else {
                            // Non-Main (tablet): LAN → Main table-server POST /tables/printOrders (Hybrid A).
                            // Do NOT call local OrdersService.printOrder (no hardwareIPC on tablet).
                            try {
                                await tableServerClient.printOrders({
                                    items: toPrint,
                                    tableName,
                                    saleNumber: saleNumForPrint,
                                    cashier,
                                    orderNumber: saleNumForPrint,
                                    separators: true
                                }, store.StoreDB);
                            } catch (lanErr) {
                                // AD-8: print failure non-fatal after save success
                                console.warn('[Tables] LAN printOrders failed (offline/table-server path, non-Main):', lanErr);
                            }
                        }
                    }
                } catch (e) {
                    console.warn('[Tables] Local order print failed (offline path):', e);
                }
                context.commit('updateSavedQty', null, { root: true });
                console.log('[TableFlow][AddOrUpdateTable:offlineDone]', {
                    traceId,
                    currentSaleAfter: context.getters.getCurrentSale
                });

                return result;
            } catch (error) {
                console.error('[Tables] Error in AddOrUpdateTable via table server:', error);

                // If we are here, both backend and table server failed
                // But if we are offline, we should have only tried table server
                // If we are online, we tried backend -> failed -> tried table server -> failed

                // If offline and error, return success message if it was queued (handled by table server client usually)
                // But tableServerClient throws on error, so we catch it here.

                throw error;
            }
        },
        updateTable: async (context, payload) => {
            // Sentry RESTAURANT-POS-Q: soft-skip concurrent saves (do not reject).
            // Duplicate clicks / route-leave + button save race were rejecting and
            // becoming unhandled Promise rejections when callers lacked .catch.
            // Callers must check result.skipped before success toast/navigation.
            if (context.state.isSavingTable) {
                console.warn('[updateTable] Already saving a table — soft-skip duplicate call');
                return { skipped: true, reason: 'already_saving' };
            }

            // Caller may pass `keepCart: true` to skip the post-save cart-clearing
            // commits. Used by PAY / DELIVERY which need the cart intact on the
            // payment screen. LOAD / SAVE omit this flag (or set false) so the
            // existing behaviour — clear cart + close the table — is preserved.
            const keepCart = payload && payload.keepCart === true;

            try {
                const traceId = `tbl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
                context.commit('setIsSavingTable', true);
                console.log('[updateTable] payload.suppressPrint ===', payload.suppressPrint);
                // console.log('[updateTable] Starting update with payload:', payload);

                let saleLines = context.getters.getSaleLines;
                let settings = await Storage.getDeviceSettings();
                let user = await Storage.getCurrentUser();
                const currentSaleNum = Number(context.getters.getCurrentSale || 0);
                const routeSaleNum = Number(router?.currentRoute?.query?.saleNumber || 0);
                const payloadSaleNum = Number(payload?.SaleNum || payload?.saleNumber || 0);
                const openTableName = String(context.getters.getOpenTableName || payload?.name || '').trim();
                let resolvedSaleNum = 0;

                if (Number.isFinite(currentSaleNum) && currentSaleNum > 0) {
                    resolvedSaleNum = Math.trunc(currentSaleNum);
                } else if (Number.isFinite(routeSaleNum) && routeSaleNum > 0) {
                    resolvedSaleNum = Math.trunc(routeSaleNum);
                } else if (Number.isFinite(payloadSaleNum) && payloadSaleNum > 0) {
                    resolvedSaleNum = Math.trunc(payloadSaleNum);
                } else if (openTableName) {
                    const tables = context.getters.getAllTables || [];
                    const match = Array.isArray(tables)
                        ? tables.find(t => String(t?.Name || t?.name || '').trim().toLowerCase() === openTableName.toLowerCase())
                        : null;
                    const tableSaleNum = Number(match?.saleNumber || match?.SaleNum || 0);
                    if (Number.isFinite(tableSaleNum) && tableSaleNum > 0) {
                        resolvedSaleNum = Math.trunc(tableSaleNum);
                    }
                }

                // Never allow update flow to allocate a brand new SaleNum.
                if (!Number.isFinite(resolvedSaleNum) || resolvedSaleNum <= 0) {
                    throw new Error('No active table selected to update. Please load the table again.');
                }

                if (!(Number.isFinite(currentSaleNum) && currentSaleNum > 0)) {
                    context.commit('setCurrentSale', resolvedSaleNum);
                }

                for (let i = 0; i < saleLines.length; i++) {
                    let line = saleLines[i];
                    saleLines[i].Line = (Number(line.Qty) * Number(line.Unit)) - Number(line.Discount);
                }
                /**
                 * Ticket #26425 Fix B/C — never synthesize "Table #n" on ordinary save.
                 * Pass previousName so AddOrUpdateTable can omit Name when unchanged.
                 * Always stamp command:"save" (default) for server empty-guard routing.
                 */
                const proposedName =
                    (payload.name && String(payload.name).trim().length > 0)
                        ? String(payload.name).trim()
                        : (openTableName || null);
                let data = {
                    SaleNum: resolvedSaleNum,
                    User: user.Username,
                    PC: settings.PC,
                    server: settings?.server || '',
                    items: saleLines,
                    Customer: payload.Customer,
                    discount: parseFloat(context.getters.getSaleDiscount.total),
                    orderNumber: payload.orderNumber || null, // Include order number if provided
                    isUpdate: true,
                    suppressPrint: payload.suppressPrint === true,
                    command: payload.command || 'save',
                    previousName: openTableName || payload.previousName || null,
                    expectedRevision: payload.expectedRevision ?? payload.revision ?? null,
                };
                if (proposedName) {
                    data.Name = proposedName;
                }
                if (payload.rename === true || payload.forceRename === true) {
                    data.rename = true;
                }

                console.log('[TableFlow][updateTable:start]', {
                    traceId,
                    currentSale: context.getters.getCurrentSale,
                    routeSale: router?.currentRoute?.query?.saleNumber,
                    dataSaleNum: data.SaleNum,
                    itemCount: Array.isArray(data.items) ? data.items.length : 0,
                    tableName: data.Name || '(omit)',
                    command: data.command,
                    suppressPrint: data.suppressPrint,
                    originalSuppressPrint: payload.suppressPrint
                });

                // console.log('[updateTable] Calling AddOrUpdateTable with SaleNum:', data.SaleNum);
                const result = await context.dispatch('AddOrUpdateTable', data);
                console.log('[TableFlow][updateTable:result]', {
                    traceId,
                    returnedSaleNumber: result?.saleNumber,
                    returnedSaleNum: result?.SaleNum,
                    currentSaleAfter: context.getters.getCurrentSale,
                    resultKeys: result ? Object.keys(result) : []
                });
                // console.log('[updateTable] Update completed successfully');
                // Clear current sale lines after successful save — unless the
                // caller asked us to keep them (PAY / DELIVERY need the cart
                // intact for the payment screen).
                if (!keepCart) {
                    context.commit('clearSale');
                    // Close the table - mark as not open
                    context.commit('setIsTabOpen', false);
                    context.commit('setOpenTableName', null);
                } else {
                    console.log('[updateTable] keepCart=true — skipping clearSale/setIsTabOpen/setOpenTableName so the cashier can proceed to the payment screen with the cart intact');
                }
                context.commit('setOpenTableName', null, { root: true });
                
                // CRITICAL: Clear the reserved sale number key so the next table gets a NEW number
                // This prevents duplicate sale numbers when creating multiple offline tables
                try {
                    let store = await Storage.getDefaultStore();
                    const reservedKey = `tableReservedSaleNum_${store?.StoreDB || 'unknown'}`;
                    localStorage.removeItem(reservedKey);
                } catch (e) {
                    console.warn('[saveNewTable] Failed to clear reserved sale number:', e);
                }
                
                return result;
            } catch (err) {
                console.error('[updateTable] Update failed:', err);
                // Timeout must surface as plain waiter language (not axios/ECONNABORTED).
                // finally below still clears isSavingTable so the lock never sticks.
                if (isAxiosTimeoutError(err)) {
                    throw new Error('Table save timed out. Check connection and try again.');
                }
                throw errorHandler.tomcatError(err);
            } finally {
                // Always clear lock — including after cloud/LAN timeout reject.
                context.commit('setIsSavingTable', false);
            }
        },
        resetTableSavingState: (context) => {
            context.commit('setIsSavingTable', false);
        },
        transferItems: async (context, payload) => {
            try {
                let settings = await Storage.getDeviceSettings();
                let user = await Storage.getCurrentUser();



                // Try to get tables from state first
                let fromTableData = context.getters.getAllTables.find(table =>
                    Number(table.saleNumber) === Number(payload.fromSaleNumber)
                );
                let toTableData = context.getters.getAllTables.find(table =>
                    Number(table.saleNumber) === Number(payload.toSaleNumber)
                );

                // If not in state, try to get from server
                if (!fromTableData) {
                    try {
                        fromTableData = await context.dispatch('getTableData', payload.fromSaleNumber);
                    } catch (error) {
                        console.error('Error getting source table:', error);
                        throw new Error(`Source table not found. Sale number: ${payload.fromSaleNumber}`);
                    }
                }

                if (!toTableData) {
                    try {
                        toTableData = await context.dispatch('getTableData', payload.toSaleNumber);
                    } catch (error) {
                        console.error('Error getting destination table:', error);
                        throw new Error(`Destination table not found. Sale number: ${payload.toSaleNumber}`);
                    }
                }

                if (!fromTableData || !toTableData) {
                    throw new Error('One or both tables could not be found');
                }

                // Format data for split table endpoint
                let splitPayload = {
                    table1: {
                        SaleNum: fromTableData.saleNumber,
                        Name: fromTableData.Name,
                        items: fromTableData.items.filter(item => !payload.items.some(transferItem => transferItem.Plu === item.Plu)),
                        pc: settings.PC,
                        User: user.Username,
                        Customer: fromTableData.customer || '',
                        discount: fromTableData.discount || 0
                    },
                    table2: {
                        SaleNum: toTableData.saleNumber,
                        Name: toTableData.Name,
                        items: [...toTableData.items, ...payload.items],
                        pc: settings.PC,
                        User: user.Username,
                        Customer: toTableData.customer || '',
                        discount: toTableData.discount || 0
                    },
                    pc: settings.PC
                };


                // Use the split table endpoint
                return await context.dispatch('splitTable', splitPayload);
            } catch (error) {
                console.error('Transfer error:', error);
                throw error;
            }
        },
        removeTable: async (context, table) => {
            const isOnline = connectivityMonitor.getStatus();
            let store = await Storage.getDefaultStore();
            let settings = await Storage.getDeviceSettings();

            context.commit(`setOverrideDetails`, table.Name);
            let isAllowed = await context.dispatch(`validateSecurityPermission`, `VoidAll`);
            if (!isAllowed) return false;

            if (settings.PrintVoid && table.saleNumber > 0) {
                let user = await Storage.getCurrentUser();
                const items = table.items.map((item) => {
                    let description = item.Description;
                    let plu = item.Plu;
                    let qty = item.Qty;
                    let value = item.Line;
                    return {
                        description,
                        plu,
                        qty,
                        value
                    }
                });
                const data = {
                    supervisor: isAllowed.supervisor,
                    items: items,
                    pc: settings.PC,
                    user: user.Username,
                    reason: 'Sale Number' + ' ' + table.saleNumber
                }
                await context.dispatch('printVoid', data);
            }

            // 1. Try Immediate Backend Sync if Online AND Main PC
            const isMainPC = await tableServerClient.checkIsMainPC();

            if (isOnline && isMainPC) {
                try {
                    await axios.delete(
                        serverApi + `tables/${store.StoreDB}/void/${table.saleNumber}`,
                        { timeout: TABLE_CLOUD_TIMEOUT_MS }
                    );

                    // Success! Now remove from local cache (table server)
                    // Mark as synced so it doesn't get queued again
                    await tableServerClient.removeTable(table.saleNumber, store.StoreDB, true);
                    return true;
                } catch (error) {
                    console.error('[Tables] Backend remove failed, falling back to offline mode:', error);
                }
            }

            // 2. Offline Mode / Fallback
            try {
                // Try table server client
                await tableServerClient.removeTable(table.saleNumber, store.StoreDB, false);
            } catch (error) {
                console.error('[Tables] Error removing via table server:', error);
                // If we are here, both failed
                throw error;
            }

            // Refresh the table list in Vuex state to reflect the deletion
            console.log('[TableFlow][removeTable] Refreshing table list after void');
            await context.dispatch('getAllTables');
            console.log('[TableFlow][removeTable] Table list refreshed, count:', context.getters.getAllTables?.length);
            // Clear open table state
            context.commit('setIsTabOpen', false);
            context.commit('setOpenTableName', null);
            context.commit('setOpenTableName', null, { root: true });
            return true;
        },
        /**
         * Load table detail by sale number.
         * @param {number|string|{saleNumber:number|string,silent?:boolean}} saleNumberOrOpts
         *   Pass `{ saleNumber, silent: true }` from empty-save hydrate so missing
         *   tables do not toast "Table Does not Exist" (WaiterStation create path).
         */
        getTableData: async (context, saleNumberOrOpts) => {
            const opts =
                saleNumberOrOpts && typeof saleNumberOrOpts === 'object'
                    ? saleNumberOrOpts
                    : { saleNumber: saleNumberOrOpts, silent: false };
            const saleNumber = opts.saleNumber ?? opts.SaleNum ?? opts.saleNum;
            const silent = opts.silent === true;

            let store = await Storage.getDefaultStore();
            const isOnline = connectivityMonitor.getStatus();

            // Offline-first: avoid server calls when disconnected.
            if (!isOnline) {
                try {
                    return await tableServerClient.getTableBySaleNumber(saleNumber, store.StoreDB);
                } catch (err) {
                    const offlineErr = new Error('Table not available offline');
                    offlineErr.code = 'TABLE_MISSING';
                    throw offlineErr;
                }
            }

            try {
                // Save-path hydrate (empty-save / 409 retry / open table). Finite
                // timeout so a hung GET cannot leave a waiter mid-save forever.
                const { data } = await axios.get(
                    franchiseTomcatApi + `savetable/${store.StoreDB}/${saleNumber}`,
                    { timeout: TABLE_CLOUD_TIMEOUT_MS }
                );
                return data;
            } catch (err) {
                // Network/server issue: fallback to local table cache.
                try {
                    return await tableServerClient.getTableBySaleNumber(saleNumber, store.StoreDB);
                } catch (_) {
                    // Silent hydrate for empty-save guard — no operator toast
                    if (silent) {
                        const bodyMsg =
                            err?.response?.data?.error ||
                            err?.response?.data?.message ||
                            err?.response?.data?.Message ||
                            err?.message ||
                            TABLE_NOT_READY_MSG;
                        const quiet = new Error(
                            isTableMissingError(err) ? TABLE_NOT_READY_MSG : String(bodyMsg)
                        );
                        quiet.code = 'TABLE_MISSING';
                        quiet.response = err?.response;
                        quiet.silent = true;
                        quiet.suppressToast = true;
                        throw quiet;
                    }
                    return errorHandler.tomcatError(err);
                }
            }
        },
        getTableBySaleNumber: async (context, saleNumber) => {
            return context.dispatch(`getTableData`, saleNumber).then(res => {
                let tabs = []
                return Promise.all([
                    context.dispatch(`openTable`, res),
                ]).then(() => res)
            }).catch((err) => {
                // Check if it's a 404 error (table doesn't exist)
                // For 404, redirect to tables list
                // For other errors, let errorHandler.tomcatError show the message and re-throw
                const status = err?.response?.status;
                if (status === 404) {
                    // Only redirect for 404 - table genuinely doesn't exist
                    router.push({ name: `tables` });
                } else {
                    // Re-throw to let errorHandler in getTableData and calling code handle it
                    throw err;
                }
            })
        },

        getOpenTables: async () => {
            let store = await Storage.getDefaultStore();
            return axios.get(serverApi + `tables/${store.StoreDB}/getAllOpenTables`, {
                timeout: TABLE_CLOUD_TIMEOUT_MS,
            })
                .then(({ data }) => data).catch(err => {
                    // errorHandler.tomcatError(err)
                    console.log(err);
                })
        },

        /**
         * Merge two or more open tables into one.
         * Ticket #26425 Fix B + D:
         *  - Require non-blank tableName (no client auto-rename / "Table #n").
         *  - Parse merge response (object or legacy "Done").
         *  - Hydrate the new sale via getTableData before allowing further Save/Pay.
         *  - Cache full items on the local table server.
         * docs/design-docs/2026-08-01-ticket-26425-client-merge-investigation.md
         * Marcus gap-analysis §5 response compatibility, §6.1 merge contract.
         *
         * @param {object} context - Vuex action context
         * @param {object} payload - { assignedTo, saleNumbers, tableName, expectedRevisions? }
         * @returns {Promise<object>} hydrated merge result { saleNum, name, items, response }
         */
        mergeTables: async (context, payload) => {
            const isOnline = connectivityMonitor.getStatus();

            // Block merge operation when offline (complex logic not implemented locally)
            if (!isOnline) {
                return responses.showWarning('Cannot merge tables while offline. Please connect to network.');
            }

            // Fix B — never invent a name; reject blank before any network call
            const confirmedTableName = requireMergeTableName(payload?.tableName ?? payload?.Name ?? payload?.name);
            const mergePayload = {
                ...payload,
                tableName: confirmedTableName,
            };

            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            mergePayload.mergedBy = user.Username;

            /**
             * After backend merge succeeds, load the authoritative table and
             * write it into the local cache so Save/Pay never sees empty items.
             * Ticket #26425 Fix D.
             *
             * @param {unknown} responseData - axios response.data or table-server body
             * @returns {Promise<object>}
             */
            const hydrateMergedSale = async (responseData) => {
                const parsed = parseMergeResponse(responseData);
                if (parsed.legacyString) {
                    // Helps detect older server talking to new client (or vice versa)
                    console.warn(
                        '[mergeTables] Legacy merge response string received (expected object with saleNum). Falling back to getAllTables/name lookup.',
                        { status: parsed.status }
                    );
                }

                let saleNum = parsed.saleNum;
                let hydrated = null;

                if (saleNum > 0) {
                    try {
                        hydrated = await context.dispatch('getTableData', saleNum);
                    } catch (e) {
                        console.warn('[mergeTables] getTableData by saleNum failed, will try list fallback', e);
                    }
                }

                if (!hydrated || extractServerTableItems(hydrated).length === 0) {
                    // Fallback: refresh open tables and find by confirmed name
                    try {
                        await context.dispatch('getAllTables');
                    } catch (_) { /* list may fail offline */ }
                    const tables = context.getters.getAllTables || [];
                    const byName = findTableByName(tables, confirmedTableName);
                    if (byName) {
                        saleNum = extractSaleNum(byName) || saleNum;
                        if (saleNum > 0) {
                            try {
                                hydrated = await context.dispatch('getTableData', saleNum);
                            } catch (_) {
                                hydrated = byName;
                            }
                        } else {
                            hydrated = byName;
                        }
                    }
                }

                const items = extractServerTableItems(hydrated);
                const serverName =
                    resolveConfirmedNameFromResponse(hydrated, confirmedTableName) ||
                    resolveConfirmedNameFromResponse(
                        typeof responseData === 'object' ? responseData : null,
                        confirmedTableName
                    ) ||
                    confirmedTableName;

                // Only adopt server name when it differs AND we did not keep operator name
                // (operator typed confirmedTableName this session — prefer it unless server differs)
                const finalName = serverName || confirmedTableName;
                saleNum = extractSaleNum(hydrated) || saleNum;

                if (saleNum > 0) {
                    const cachePayload = {
                        ...(hydrated && typeof hydrated === 'object' ? hydrated : {}),
                        SaleNum: saleNum,
                        saleNumber: saleNum,
                        Name: finalName,
                        name: finalName,
                        items,
                        User: mergePayload.assignedTo || user.Username,
                        synced: true,
                        isOffline: false,
                    };
                    try {
                        await tableServerClient.addOrUpdateTable(cachePayload, store.StoreDB);
                    } catch (cacheErr) {
                        console.warn('[mergeTables] Failed to cache hydrated merged table locally:', cacheErr);
                    }
                }

                console.log('[mergeTables:hydrated]', {
                    saleNum,
                    name: finalName,
                    itemCount: items.length,
                    legacyString: parsed.legacyString,
                });

                return {
                    saleNum,
                    name: finalName,
                    items,
                    revision: parsed.revision,
                    itemCount: parsed.itemCount != null ? parsed.itemCount : items.length,
                    response: responseData,
                    legacyString: parsed.legacyString,
                };
            };

            // 1. Try Immediate Backend Sync if Online AND Main PC
            const isMainPC = await tableServerClient.checkIsMainPC();

            if (isOnline && isMainPC) {
                const response = await axios.post(
                    serverApi + `tables/${store.StoreDB}/merge`,
                    mergePayload,
                    { timeout: TABLE_CLOUD_TIMEOUT_MS }
                );
                const responseData = response?.data !== undefined ? response.data : response;

                // Remove parent local rows (synced path) then hydrate new sale
                const localPayload = { ...mergePayload, synced: true };
                try {
                    await tableServerClient.mergeTables(localPayload, store.StoreDB);
                } catch (localMergeErr) {
                    console.warn('[mergeTables] Local parent cleanup failed (continuing hydrate):', localMergeErr);
                }

                const hydrated = await hydrateMergedSale(responseData);
                return { ...response, data: hydrated, hydrated };
            }

            // Not Main PC — route through table server (Main proxies merge)
            if (isOnline) {
                const tsResult = await tableServerClient.mergeTables(mergePayload, store.StoreDB);
                const responseData = tsResult?.data !== undefined ? tsResult.data : tsResult;
                const hydrated = await hydrateMergedSale(responseData);
                return { data: hydrated, hydrated };
            }
        },

        transferTable: async (context, payload) => {
            const isOnline = connectivityMonitor.getStatus();

            // Block transfer operation when offline
            if (!isOnline) {
                return responses.showWarning('Cannot transfer tables while offline. Please connect to network.');
            }

            let user = await Storage.getCurrentUser();
            user = user.Username;
            let isAllowed = await context.dispatch(`validateSecurityPermission`, `transferTable`);
            if (!isAllowed) return false;

            $utils.showLoading();
            let store = await Storage.getDefaultStore();
            let settings = await Storage.getDeviceSettings();
            payload.transferredBy = user;

            try {
                // 1. Try Immediate Backend Sync if Online AND Main PC
                const isMainPC = await tableServerClient.checkIsMainPC();

                if (isOnline && isMainPC) {
                    try {
                        await axios.put(
                            serverApi + `tables/${store.StoreDB}/transfer`,
                            payload,
                            { timeout: TABLE_CLOUD_TIMEOUT_MS }
                        );

                        // Success! Now update local cache
                        const localPayload = { ...payload, synced: true };
                        await tableServerClient.transferTable(localPayload, store.StoreDB);

                        return true;
                    } catch (error) {
                        console.error('[Tables] Backend transfer failed:', error);
                        throw error;
                    }
                }
            } catch (error) {
                errorHandler.tomcatError(error);
            } finally {
                $utils.hideLoading();
            }
        },
        initiateEmptyTable: async () => {
            let user = await Storage.getCurrentUser();
            user = user.Username;

            let settings = await Storage.getDeviceSettings();
            let pc = settings.PC;
            let store = await Storage.getDefaultStore();
            let storeDb = store.StoreDB;
            let obj = {
                User: user,
                PC: pc,
            }

            return axios.post(
                serverApi + `tables/${storeDb}/initiateEmptyTable`,
                obj,
                { timeout: TABLE_CLOUD_TIMEOUT_MS }
            ).then(({ data }) => data)
                .catch(err => errorHandler.tomcatError(err))
        },
        async splitTable(context, payload) {
            const isOnline = connectivityMonitor.getStatus();

            // Block split operation when offline
            if (!isOnline) {
                return responses.showWarning('Cannot split tables while offline. Please connect to network.');
            }

            try {
                let store = await Storage.getDefaultStore();
                let user = await Storage.getCurrentUser();
                let settings = await Storage.getDeviceSettings();
                user = user.Username;
                let obj = {
                    splitBy: user,
                    ...payload
                };

                // 1. Try Immediate Backend Sync if Online AND Main PC
                const isMainPC = await tableServerClient.checkIsMainPC();

                if (isOnline && isMainPC) {
                    try {
                        const response = await axios.post(
                            serverApi + `tables/${store.StoreDB}/split`,
                            obj,
                            { timeout: TABLE_CLOUD_TIMEOUT_MS }
                        );

                        // Success! Now update local cache
                        const localPayload = { ...obj, synced: true };
                        await tableServerClient.splitTable(localPayload, store.StoreDB);

                        await context.commit('setCurrentLines', []);
                        return response;
                    } catch (error) {
                        console.error('[Tables] Backend split failed:', error);
                        throw error;
                    }
                }

                // Offline fallback (though currently blocked above)
                // Try table server client
                const response = await tableServerClient.splitTable(obj, store.StoreDB);
                await context.commit('setCurrentLines', []);
                return response;
            } catch (error) {
                console.error('Split table error:', error.response || error);
                throw error;
            }
        },
        proceedToSales(context) {
            let settings = context.getters.getDeviceSettings;
            let autoLogout = settings.AutoLog === 1;
            const path = router.currentRoute.path;
            if (autoLogout) {
                if (path === '/poslogin') return;
                return context.dispatch("posLogout");
            }
            if (settings.PosType === `Retail` || settings.PosType === `Touch`) {
                if (path === '/pos') return;
                return router.push({ name: `pos` })
            }
            if (path === '/tables') return;
            return router.push({ name: `tables` })
        },
        goToSales: (context) => {
            let settings = context.getters.getDeviceSettings;
            if (settings.PosType === `Retail` || settings.PosType === `Touch`) {
                return router.push({ name: `pos` })
            }
            return router.push({ name: `tables` })
        },
        async checkForOpenTables({ commit, rootState }) {
            try {
                let user = await Storage.getCurrentUser();
                let store = await Storage.getDefaultStore();
                let storeDb = store.StoreDB;

                user = user.Username;

                const response = await axios.get(
                    serverApi + `tables/${storeDb}/countOpenTables/${user}`,
                    { timeout: TABLE_CLOUD_TIMEOUT_MS }
                );
                (response)
                if (response.data > 0) {
                    return {
                        hasOpenTables: true,
                        count: response.data
                    };
                }
                return {
                    hasOpenTables: false,
                    count: 0
                };
            } catch (error) {
                console.error('Error checking for open tables:', error);
                throw error;
            }
        },

    }
}
